import { useEffect, useState } from "react";
import { Scissors, CalendarCheck, Ban } from "lucide-react";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const getDeviceId = () => {
  let d = localStorage.getItem("bl_staff_device");
  if (!d) {
    d = crypto.randomUUID();
    localStorage.setItem("bl_staff_device", d);
  }
  return d;
};

const TIME_SLOTS = (() => {
  const out = [];
  for (const [from, to] of [[9 * 60, 13 * 60 + 30], [15 * 60 + 30, 20 * 60 + 30]]) {
    for (let m = from; m + 30 <= to; m += 30) {
      out.push(`${String(Math.floor(m / 60)).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`);
    }
  }
  return out;
})();
const IT_GIORNI = ["domenica", "lunedì", "martedì", "mercoledì", "giovedì", "venerdì", "sabato"];
const IT_MESI = [
  "gennaio", "febbraio", "marzo", "aprile", "maggio", "giugno",
  "luglio", "agosto", "settembre", "ottobre", "novembre", "dicembre",
];
const toGiorno = (dateStr) => {
  const d = new Date(`${dateStr}T12:00:00`);
  return `${IT_GIORNI[d.getDay()]} ${d.getDate()} ${IT_MESI[d.getMonth()]}`;
};

const INPUT =
  "h-11 w-full rounded-xl border border-white/10 bg-zinc-950 px-3 text-sm text-white outline-none placeholder:text-zinc-600 focus:border-amber-400/50";

export default function StaffPage({ token }) {
  const [deviceId] = useState(getDeviceId);
  const [info, setInfo] = useState(null);
  const [denied, setDenied] = useState(null);
  const [form, setForm] = useState({ nome: "", telefono: "", servizio: "", barbiere: "", data: "", ora: "" });
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(null);
  const [status, setStatus] = useState(null);

  const loadStatus = async () => {
    try {
      const r = await fetch(`${BACKEND}/api/staff/${token}/status`, {
        headers: { "X-Device-Id": deviceId },
        credentials: "include",
      });
      if (r.ok) setStatus(await r.json());
    } catch {
      // reintenta en el próximo intervalo
    }
  };

  useEffect(() => {
    if (!info) return undefined;
    loadStatus();
    const iv = setInterval(loadStatus, 30000);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [info]);

  const completeBooking = async (id) => {
    const r = await fetch(`${BACKEND}/api/staff/${token}/booking/${id}/complete`, {
      method: "POST",
      headers: { "X-Device-Id": deviceId },
      credentials: "include",
    });
    if (r.ok) loadStatus();
  };

  useEffect(() => {
    fetch(`${BACKEND}/api/staff/${token}`, {
      headers: { "X-Device-Id": deviceId },
      credentials: "include",
    })
      .then(async (r) => {
        if (r.ok) {
          setInfo(await r.json());
        } else {
          const d = await r.json().catch(() => ({}));
          setDenied(typeof d.detail === "string" ? d.detail : "Link non valido");
        }
      })
      .catch(() => setDenied("Errore di rete"));
  }, [token, deviceId]);

  const save = async () => {
    if (saving) return;
    setError("");
    if (!form.nome.trim() || !form.telefono.trim() || !form.servizio || !form.data || !form.ora) {
      setError("Completa nome, telefono, servizio, giorno e ora.");
      return;
    }
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND}/api/staff/${token}/booking`, {
        method: "POST",
        headers: { "Content-Type": "application/json", "X-Device-Id": deviceId },
        credentials: "include",
        body: JSON.stringify({ ...form, giorno: toGiorno(form.data) }),
      });
      if (res.ok) {
        const doc = await res.json();
        setDone(doc);
        setForm({ nome: "", telefono: "", servizio: "", barbiere: "", data: "", ora: "" });
      } else {
        const d = await res.json().catch(() => ({}));
        setError(typeof d.detail === "string" ? d.detail : "Errore nel salvataggio");
      }
    } catch {
      setError("Errore di rete: riprova.");
    }
    setSaving(false);
  };

  return (
    <div className="min-h-[100dvh] bg-zinc-950 text-white">
      <div className="mx-auto flex min-h-[100dvh] w-full max-w-md flex-col gap-6 px-6 py-8">
        <header className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-2xl bg-amber-400/15 text-amber-400">
            <Scissors size={20} />
          </span>
          <div>
            <h1 className="font-display text-2xl font-bold">Barber Latin Staff</h1>
            <p className="text-xs text-zinc-500">Prenotazioni rapide dal team</p>
          </div>
        </header>

        {!info && !denied && <p className="text-sm text-zinc-500">Caricamento…</p>}

        {denied && (
          <div
            data-testid="staff-denied"
            className="flex flex-col items-center gap-3 rounded-2xl border border-red-500/30 bg-red-500/10 p-8 text-center"
          >
            <Ban size={28} className="text-red-400" />
            <p className="text-sm font-bold text-red-300">{denied}</p>
            <p className="text-xs text-zinc-500">
              Chiedi al titolare di riattivare o condividere di nuovo il link.
            </p>
          </div>
        )}

        {info && status && (
          <div data-testid="staff-status" className="flex flex-col gap-3">
            {status.stato === "completato" ? (
              <div
                data-testid="staff-celebration"
                className="flex flex-col items-center gap-2 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-6 text-center"
              >
                <p className="text-3xl">🎉💈🔥</p>
                <p className="font-display text-lg font-bold text-amber-300">
                  Obiettivo raggiunto!
                </p>
                <p className="text-xs text-zinc-400">
                  Hai completato tutti i servizi di oggi. Grande lavoro, {status.barbiere}!
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-3 rounded-2xl border border-white/10 bg-zinc-900 p-4">
                <span
                  data-testid="staff-dot"
                  className={`h-3.5 w-3.5 shrink-0 rounded-full ${
                    status.stato === "in_orario"
                      ? "animate-pulse bg-emerald-400"
                      : status.stato === "in_ritardo"
                        ? "animate-pulse bg-red-500"
                        : "bg-emerald-400"
                  }`}
                />
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold text-white">
                    {status.stato === "in_orario" && `In orario con ${status.cliente}`}
                    {status.stato === "in_ritardo" && `In ritardo di ${status.minuti} min`}
                    {status.stato === "libero" && "Disponibile"}
                  </p>
                  <p className="truncate text-[11px] text-zinc-400">
                    {status.stato === "in_orario" &&
                      `${status.servizio} — finisce alle ${status.fine}`}
                    {status.stato === "in_ritardo" &&
                      `${status.cliente} (${status.servizio}) — premi Fatto quando finisci`}
                    {status.stato === "libero" && "Nessun servizio in corso"}
                  </p>
                </div>
                <span className="shrink-0 text-[10px] font-bold text-zinc-500">
                  {status.oggi_fatti}/{status.oggi_fatti + status.oggi_rimasti} oggi
                </span>
              </div>
            )}

            {(status.stato === "in_orario" || status.stato === "in_ritardo") && status.booking_id && (
              <button
                data-testid="staff-done-current"
                onClick={() => completeBooking(status.booking_id)}
                className="flex items-center justify-center gap-2 rounded-2xl bg-emerald-500 py-4 text-sm font-extrabold uppercase tracking-widest text-white shadow-[0_0_20px_rgba(16,185,129,0.35)] transition-[background-color,transform] duration-200 hover:bg-emerald-400 active:scale-[0.97]"
              >
                Fatto — termina con {status.cliente} ✓
              </button>
            )}

            {status.prenotazioni_oggi.length > 0 && (              <div className="flex flex-col gap-2">
                <p className="text-[10px] font-bold uppercase tracking-widest text-zinc-500">
                  I tuoi servizi di oggi — rimasti: {status.oggi_rimasti}
                </p>
                {status.prenotazioni_oggi.map((p) => (
                  <div
                    key={p.id}
                    data-testid={`staff-today-${p.id}`}
                    className="flex items-center gap-3 rounded-xl border border-white/5 bg-zinc-900 px-3 py-2.5"
                  >
                    <span className="shrink-0 text-xs font-bold text-amber-300">{p.ora}</span>
                    <span className="min-w-0 flex-1 truncate text-xs text-zinc-200">
                      {p.cliente} · {p.servizio}
                      {status.owner && p.telefono && (
                        <span className="block text-[11px] font-bold text-zinc-400">
                          Tel: {p.telefono}
                        </span>
                      )}
                    </span>
                    {status.owner && p.telefono && (
                      <>
                        <a
                          data-testid={`staff-call-${p.id}`}
                          href={`tel:${p.telefono}`}
                          className="flex h-8 shrink-0 items-center rounded-full bg-emerald-500/15 px-3 text-[10px] font-extrabold uppercase tracking-widest text-emerald-400 transition-colors hover:bg-emerald-500/25"
                        >
                          Chiama
                        </a>
                        <a
                          data-testid={`staff-remind-${p.id}`}
                          href={`https://wa.me/${(() => {
                            const d = (p.telefono || "").replace(/\D/g, "");
                            return d.startsWith("39") ? d : `39${d}`;
                          })()}?text=${encodeURIComponent(
                            `Ciao ${p.cliente}! Ti ricordiamo il tuo appuntamento oggi alle ${p.ora} da Barber Latin. A presto!`
                          )}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex h-8 shrink-0 items-center rounded-full bg-blue-500/15 px-3 text-[10px] font-extrabold uppercase tracking-widest text-blue-300 transition-colors hover:bg-blue-500/25"
                        >
                          Avvisa
                        </a>
                      </>
                    )}
                    {p.status === "completata" ? (
                      <span className="shrink-0 text-[10px] font-extrabold uppercase tracking-widest text-emerald-400">
                        Fatto ✓
                      </span>
                    ) : (
                      <button
                        data-testid={`staff-done-${p.id}`}
                        onClick={() => completeBooking(p.id)}
                        className="h-8 shrink-0 rounded-full bg-emerald-500 px-3 text-[10px] font-extrabold uppercase tracking-widest text-white transition-colors hover:bg-emerald-400"
                      >
                        Fatto
                      </button>
                    )}
                  </div>
                ))}
              </div>
            )}

            {status.stelle && (
              <div
                data-testid="staff-stars"
                className="flex flex-col gap-3 rounded-2xl border border-amber-400/30 bg-amber-400/5 p-4"
              >
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-300">
                  Le tue stelle della settimana
                </p>
                <p data-testid="staff-stars-current" className="text-3xl tracking-widest">
                  {"⭐".repeat(status.stelle[0].stelle)}
                  <span className="opacity-25">{"⭐".repeat(5 - status.stelle[0].stelle)}</span>
                </p>
                {status.fine_settimana && (
                  <p
                    data-testid="staff-weekend-cheer"
                    className="rounded-xl bg-amber-400/15 px-3 py-2 text-center text-xs font-bold text-amber-200"
                  >
                    Settimana finita — bravo! 🎊🎉💈 Ci vediamo la prossima settimana ⭐
                  </p>
                )}
                <div className="flex flex-col gap-1.5 border-t border-white/5 pt-2">
                  {status.stelle.slice(1).map((w) => (
                    <div key={w.label} className="flex items-center justify-between">
                      <span className="text-[11px] text-zinc-500">{w.label}</span>
                      <span className="text-sm tracking-wider">
                        {"⭐".repeat(w.stelle)}
                        <span className="opacity-20">{"⭐".repeat(5 - w.stelle)}</span>
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {info && status?.owner && (
          <p
            data-testid="staff-owner-mode"
            className="rounded-xl border border-blue-500/30 bg-blue-500/10 px-3 py-2 text-center text-[11px] font-bold uppercase tracking-widest text-blue-300"
          >
            Vista titolare — agenda di {info.nome}
          </p>
        )}

        {info && status?.agenda_piena && (
          <div
            data-testid="staff-full"
            className="flex flex-col items-center gap-2 rounded-2xl border border-amber-400/40 bg-amber-400/10 p-6 text-center"
          >
            <p className="text-2xl">🔒⭐</p>
            <p className="font-display text-lg font-bold text-amber-300">Agenda piena!</p>
            <p className="text-xs text-zinc-400">
              Hai completato la tua agenda di oggi. Solo il titolare può modificare o eliminare
              prenotazioni.
            </p>
          </div>
        )}

        {info && !done && !status?.agenda_piena && (
          <div data-testid="staff-form" className="flex flex-col gap-3">
            <p className="text-xs font-bold uppercase tracking-widest text-amber-300">
              Ciao {info.nome}, registra una prenotazione
            </p>
            <input
              data-testid="staff-nome"
              value={form.nome}
              onChange={(e) => setForm({ ...form, nome: e.target.value })}
              placeholder="Nome e cognome del cliente"
              className={INPUT}
            />
            <input
              data-testid="staff-telefono"
              value={form.telefono}
              onChange={(e) => setForm({ ...form, telefono: e.target.value })}
              placeholder="Telefono del cliente (obbligatorio)"
              className={INPUT}
            />
            <div className="flex gap-2">
              <select
                data-testid="staff-servizio"
                value={form.servizio}
                onChange={(e) => setForm({ ...form, servizio: e.target.value })}
                className={`${INPUT} min-w-0 flex-1`}
              >
                <option value="">Servizio…</option>
                {info.services.map((s) => (
                  <option key={s.id} value={s.nome}>
                    {s.nome} — {s.prezzo}€
                  </option>
                ))}
              </select>
              <select
                data-testid="staff-barbiere"
                value={form.barbiere}
                onChange={(e) => setForm({ ...form, barbiere: e.target.value })}
                className={`${INPUT} min-w-0 flex-1`}
              >
                <option value="">Barbiere…</option>
                {info.team.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex gap-2">
              <input
                data-testid="staff-data"
                type="date"
                value={form.data}
                onChange={(e) => setForm({ ...form, data: e.target.value })}
                className={`${INPUT} min-w-0 flex-1`}
              />
              <select
                data-testid="staff-ora"
                value={form.ora}
                onChange={(e) => setForm({ ...form, ora: e.target.value })}
                className={`${INPUT} min-w-0 flex-1`}
              >
                <option value="">Ora…</option>
                {TIME_SLOTS.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            {error && (
              <p data-testid="staff-error" className="text-xs font-semibold text-red-400">
                {error}
              </p>
            )}
            <button
              data-testid="staff-save"
              onClick={save}
              disabled={saving}
              className="h-12 rounded-xl bg-amber-400 text-xs font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300 disabled:opacity-40"
            >
              {saving ? "Salvataggio…" : "Salva prenotazione"}
            </button>
          </div>
        )}

        {done && (
          <div
            data-testid="staff-done"
            className="flex flex-col items-center gap-3 rounded-2xl border border-emerald-500/30 bg-emerald-500/10 p-8 text-center"
          >
            <CalendarCheck size={28} className="text-emerald-400" />
            <p className="text-sm font-bold text-emerald-300">Prenotazione registrata</p>
            <p className="text-xs text-zinc-400">
              {done.nome} — {done.servizio}, {done.giorno} alle {done.ora}
              {done.barbiere ? ` · ${done.barbiere}` : ""}
            </p>
            <p className="text-[11px] text-zinc-600">È già nell&apos;agenda del titolare.</p>
            <button
              data-testid="staff-another"
              onClick={() => setDone(null)}
              className="mt-2 rounded-full bg-amber-400 px-5 py-2 text-[11px] font-extrabold uppercase tracking-widest text-zinc-950 transition-colors hover:bg-amber-300"
            >
              Nuova prenotazione
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
